import { useState } from 'react'

import { FormattedMessage } from 'react-intl'

import ScrolledContainer from 'app/components/base/Scroll/ScrolledContainer'
import Stack from 'app/components/base/Stack'
import Text from 'app/components/base/Text'
import useCurrencyState from 'app/hooks/useCurrencyState'
import useAppStore from 'app/store/app.store'
import { IS_NATIVE_APP } from 'app/utils/constants/env.constants'
import { getFirstUrlAttachment } from 'app/utils/helpers/functions.helpers'

import NavigationSlideButton from '../components/NavigationSlideButton'

import SimilarItemCard from './components/SimilarItemCard'

interface SimilarItemsProps {
  similarItems: ListingModel[]
  isMobile?: boolean
  isLargeSize?: boolean
}

const SimilarItems: RFC<SimilarItemsProps> = ({
  similarItems,
  isMobile,
  isLargeSize
}) => {
  const [startIndex, setStartIndex] = useState(0)
  const { currency } = useCurrencyState()
  const isTablet = useAppStore((state) => state.isTablet)

  const desktopMaxCards = isLargeSize ? 1 : 2
  const maxCards = isMobile ? 4 : desktopMaxCards
  const showButtons = similarItems.length > maxCards
  const currentCards = showButtons ? maxCards : similarItems.length

  const endIndex = startIndex + currentCards
  const isPrevButtonDisabled = startIndex === 0
  const isNextButtonDisabled = endIndex === similarItems.length

  return (
    <Stack
      direction="column"
      sx={{
        width: { xs: '100%', lg: 500 },
        maxWidth: { md: isTablet ? '100%' : 400, lg: 700 }
      }}
    >
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Text variant="h3">
          <FormattedMessage defaultMessage="Similar Items" />
        </Text>

        {showButtons && (
          <Stack spacing={1} direction="row">
            <NavigationSlideButton
              rotate
              disabled={isPrevButtonDisabled}
              onClick={() => setStartIndex((prev) => prev - 1)}
            />

            <NavigationSlideButton
              disabled={isNextButtonDisabled}
              onClick={() => setStartIndex((prev) => prev + 1)}
            />
          </Stack>
        )}
      </Stack>

      <ScrolledContainer
        disable={!isMobile}
        horizontal
        sx={{ mr: { xs: -2, md: 0 } }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            pt: 2,
            pb: 3,
            px: IS_NATIVE_APP ? 0 : '1px',
            ...(!IS_NATIVE_APP && { width: 'fit-content' })
          }}
        >
          {similarItems
            .slice(startIndex, startIndex + currentCards)
            .map((listing) => {
              const link = `/listing/${listing?.slug}`
              const image = getFirstUrlAttachment(listing?.attachments) || ''
              const price =
                listing.converted_actual_price || listing.actual_price

              return (
                <SimilarItemCard
                  key={listing.id}
                  title={listing.title}
                  image={image}
                  link={link}
                  price={price}
                  currency={currency}
                />
              )
            })}
        </Stack>
      </ScrolledContainer>
    </Stack>
  )
}

export default SimilarItems
